import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Video, Plus, Copy, ExternalLink, Mail, Users, Loader2, Settings2,
  Pencil, XCircle, AlertTriangle, Link2, PhoneCall, ChevronRight, CalendarClock,
} from "lucide-react";
import ScheduleMeetingDialog from "../components/meetings/ScheduleMeetingDialog";
import MeetingIntegrationsCard from "../components/meetings/MeetingIntegrationsCard";
import { formatMeetingWindow } from "../lib/meetingTime";

const TABS = [
  { id: "upcoming", label: "Upcoming" },
  { id: "past", label: "Past" },
  { id: "setup", label: "Connections" },
];

const EMAIL_BADGE = {
  sent: { label: "Invite sent", className: "text-emerald-700" },
  not_sent: { label: "No invite sent", className: "text-muted-foreground" },
  failed: { label: "Invite failed", className: "text-red-700" },
  skipped: { label: "Invite skipped", className: "text-muted-foreground" },
};

function ProviderIcon({ meeting, className = "w-4 h-4" }) {
  if (meeting.zoom_meeting_id) return <Video className={className} />;
  if (meeting.join_url) return <Link2 className={className} />;
  return <PhoneCall className={className} />;
}

function MeetingRow({ meeting, isPast, onEdit, onCancel, onResend }) {
  const [copying, setCopying] = useState(false);
  const cancelled = meeting.status === "cancelled";
  const badge = EMAIL_BADGE[meeting.email_status] || EMAIL_BADGE.not_sent;

  const copyLink = async () => {
    if (!meeting.join_url) return;
    try {
      await navigator.clipboard.writeText(meeting.join_url);
      setCopying(true);
      toast.success("Link copied");
      setTimeout(() => setCopying(false), 1200);
    } catch {
      toast.error("Could not copy the link");
    }
  };

  return (
    <Card
      className={`border border-border p-4 sm:p-5 ${cancelled ? "opacity-60" : ""}`}
      data-testid="meeting-row"
    >
      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <ProviderIcon meeting={meeting} className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="text-base text-foreground truncate">{meeting.title}</span>
            {cancelled && (
              <span className="text-[10px] uppercase tracking-[1.5px] text-red-700">Cancelled</span>
            )}
          </div>

          <p className="text-sm text-foreground mt-1.5">
            {formatMeetingWindow(meeting.starts_at, meeting.duration_minutes, meeting.timezone, {
              withYear: true,
            })}
          </p>

          <div className="flex items-center gap-x-3 gap-y-1 flex-wrap mt-2 text-xs text-muted-foreground">
            {meeting.program_id ? (
              <Link
                to={`/programs/${meeting.program_id}`}
                className="inline-flex items-center gap-1 hover:text-foreground"
              >
                {meeting.program_name || "Project"}
                <ChevronRight className="w-3 h-3" />
              </Link>
            ) : null}
            {meeting.client_name && <span>{meeting.client_name}</span>}
            <span>Host: {meeting.host_name || "—"}</span>
            {meeting.attendees?.length > 0 && (
              <span className="inline-flex items-center gap-1">
                <Users className="w-3 h-3" />
                {meeting.attendees.length} invited
              </span>
            )}
            <span className={badge.className}>{badge.label}</span>
          </div>

          {meeting.attendees?.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1.5 truncate">
              {meeting.attendees.map((a) => a.name || a.email).join(", ")}
            </p>
          )}

          {/* Only a real send failure is worth flagging — "skipped" already reads
              as its own badge above, so repeating its reason just adds noise. */}
          {meeting.email_status === "failed" && meeting.last_email_error && (
            <p className="text-xs text-red-700 mt-1.5 flex items-start gap-1">
              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
              {meeting.last_email_error}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {meeting.join_url && !cancelled && (
            <>
              <Button size="sm" variant="outline" onClick={copyLink} data-testid="meeting-copy-link">
                <Copy className="w-3.5 h-3.5 mr-1.5" />
                {copying ? "Copied" : "Copy link"}
              </Button>
              <Button size="sm" asChild>
                <a href={meeting.start_url || meeting.join_url} target="_blank" rel="noreferrer">
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  {isPast ? "Open" : "Join"}
                </a>
              </Button>
            </>
          )}
          {!cancelled && !isPast && (
            <>
              <Button size="sm" variant="outline" onClick={() => onResend(meeting)} data-testid="meeting-resend">
                <Mail className="w-3.5 h-3.5 mr-1.5" /> Resend
              </Button>
              <Button size="sm" variant="outline" onClick={() => onEdit(meeting)} data-testid="meeting-edit">
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onCancel(meeting)}
                className="text-red-700 hover:text-red-800"
                data-testid="meeting-cancel"
              >
                <XCircle className="w-3.5 h-3.5 mr-1.5" /> Cancel
              </Button>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function Meetings() {
  const [searchParams, setSearchParams] = useSearchParams();
  // ?tab=setup lands people straight on the connections tab — that's the shortcut
  // the booking dialog and the sidebar nudge both point at.
  const [tab, setTab] = useState(
    TABS.some((t) => t.id === searchParams.get("tab")) ? searchParams.get("tab") : "upcoming"
  );
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState([]);
  const [mineOnly, setMineOnly] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [resendTarget, setResendTarget] = useState(null);
  const [resendNote, setResendNote] = useState("");
  const [resending, setResending] = useState(false);

  const scope = tab === "past" ? "past" : "upcoming";

  const load = useCallback(async () => {
    if (tab === "setup") {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.get("/meetings", {
        params: { scope, mine: mineOnly, include_cancelled: scope === "past" },
      });
      setMeetings(res.data?.items || []);
    } catch (e) {
      toast.error("Could not load meetings");
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  }, [scope, mineOnly, tab]);

  useEffect(() => {
    load();
  }, [load]);

  const loadIntegrations = useCallback(async () => {
    try {
      const res = await apiClient.get("/meetings/integrations");
      setIntegrations(res.data?.items || []);
    } catch {
      setIntegrations([]);
    }
  }, []);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  // Keep ?tab= in step with the active tab so the connections view is linkable.
  const selectTab = useCallback(
    (next) => {
      setTab(next);
      const params = new URLSearchParams(searchParams);
      if (next === "upcoming") params.delete("tab");
      else params.set("tab", next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const zoomReady = useMemo(
    () => integrations.some((i) => i.provider === "zoom" && i.connected && i.status === "connected"),
    [integrations]
  );
  const anythingConfigured = useMemo(
    () => integrations.some((i) => i.configured),
    [integrations]
  );

  const doCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const res = await apiClient.post(`/meetings/${cancelTarget.id}/cancel`, {
        reason: cancelReason || null,
        notify: true,
      });
      (res.data?.warnings || []).forEach((w) => toast.warning(w));
      toast.success(res.data?.notified ? "Cancelled — attendees notified" : "Meeting cancelled");
      setCancelTarget(null);
      setCancelReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel the meeting");
    } finally {
      setCancelling(false);
    }
  };

  const doResend = async () => {
    if (!resendTarget) return;
    setResending(true);
    try {
      await apiClient.post(`/meetings/${resendTarget.id}/send-invite`, {
        email_message: resendNote || null,
        cc_host: true,
      });
      toast.success("Invite sent again");
      setResendTarget(null);
      setResendNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend the invite");
    } finally {
      setResending(false);
    }
  };

  const openSchedule = () => {
    setEditing(null);
    setScheduleOpen(true);
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="meetings-view">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 md:mb-8">
        <div>
          <h1 className="heading-1 text-foreground">Meetings</h1>
          <p className="body-text mt-1">
            Book the call, create the link and send the invite — without leaving HQ.
          </p>
        </div>
        <Button onClick={openSchedule} data-testid="book-meeting-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Book a meeting
        </Button>
      </div>

      {/* Nudge to connect Zoom */}
      {!zoomReady && anythingConfigured && tab !== "setup" && (
        <Card className="border border-border p-4 mb-6 bg-secondary/40">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Video className="w-5 h-5 text-muted-foreground shrink-0" />
            <p className="text-sm text-muted-foreground flex-1">
              Connect your Zoom account and HQ will create the meeting for you. Until then you can
              still book a meeting and paste a link yourself.
            </p>
            <Button size="sm" variant="outline" onClick={() => selectTab("setup")}>
              <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Connect
            </Button>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-1 p-1 bg-secondary/50 rounded-lg w-full sm:w-fit overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => selectTab(t.id)}
              data-testid={`meetings-tab-${t.id}`}
              className={`px-4 py-2 rounded-md text-sm transition-all flex-1 sm:flex-none ${
                tab === t.id
                  ? "bg-white text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab !== "setup" && (
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={mineOnly}
              onChange={(e) => setMineOnly(e.target.checked)}
              className="rounded border-border"
              data-testid="meetings-mine-only"
            />
            Only mine
          </label>
        )}
      </div>

      {/* Body */}
      {tab === "setup" ? (
        <div className="space-y-4">
          <div>
            <h2 className="heading-2 text-foreground">Your connections</h2>
            <p className="body-text mt-1">
              These are per person — each connection uses your own account, so meetings are hosted
              by you and land on your calendar.
            </p>
          </div>
          <MeetingIntegrationsCard returnTo="/meetings" onChange={setIntegrations} />
        </div>
      ) : loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-12 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading meetings…
        </div>
      ) : meetings.length === 0 ? (
        <Card className="border border-border border-dashed p-10 text-center">
          <CalendarClock className="w-8 h-8 mx-auto text-muted-foreground/60" />
          <p className="text-base text-foreground mt-3">
            {scope === "past" ? "No past meetings yet" : "Nothing on the books"}
          </p>
          <p className="body-text mt-1 max-w-md mx-auto">
            {scope === "past"
              ? "Meetings move here once they've happened."
              : "Book a meeting and HQ will create the Zoom link, add it to your calendar and email the client the invite."}
          </p>
          {scope !== "past" && (
            <Button className="mt-5" onClick={openSchedule}>
              <Plus className="w-4 h-4 mr-1.5" /> Book a meeting
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-3">
          {meetings.map((m) => (
            <MeetingRow
              key={m.id}
              meeting={m}
              isPast={scope === "past"}
              onEdit={(mt) => {
                setEditing(mt);
                setScheduleOpen(true);
              }}
              onCancel={(mt) => {
                setCancelTarget(mt);
                setCancelReason("");
              }}
              onResend={(mt) => {
                setResendTarget(mt);
                setResendNote("");
              }}
            />
          ))}
        </div>
      )}

      <ScheduleMeetingDialog
        open={scheduleOpen}
        onOpenChange={(v) => {
          setScheduleOpen(v);
          if (!v) setEditing(null);
        }}
        integrations={integrations}
        onIntegrationsChanged={loadIntegrations}
        meeting={editing}
        onScheduled={() => {
          load();
          loadIntegrations();
        }}
      />

      {/* Cancel */}
      <Dialog open={Boolean(cancelTarget)} onOpenChange={(v) => !v && setCancelTarget(null)}>
        <DialogContent data-testid="cancel-meeting-dialog">
          <DialogHeader>
            <DialogTitle>Cancel this meeting?</DialogTitle>
            <DialogDescription>
              The Zoom meeting is deleted, the calendar entry is cancelled and attendees get a
              cancellation email their calendar can read.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-foreground">{cancelTarget?.title}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {cancelTarget &&
                formatMeetingWindow(
                  cancelTarget.starts_at,
                  cancelTarget.duration_minutes,
                  cancelTarget.timezone,
                  { withYear: true }
                )}
            </p>
            <Input
              className="mt-4"
              placeholder="Reason (optional — included in the email)"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              data-testid="cancel-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)} disabled={cancelling}>
              Keep it
            </Button>
            <Button
              onClick={doCancel}
              disabled={cancelling}
              className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="confirm-cancel-meeting"
            >
              {cancelling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Cancel meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend */}
      <Dialog open={Boolean(resendTarget)} onOpenChange={(v) => !v && setResendTarget(null)}>
        <DialogContent data-testid="resend-invite-dialog">
          <DialogHeader>
            <DialogTitle>Send the invite again</DialogTitle>
            <DialogDescription>
              Goes to everyone on the invite, with the calendar attachment re-attached.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-xs text-muted-foreground">
              {(resendTarget?.attendees || []).map((a) => a.name || a.email).join(", ") ||
                "No attendees yet"}
            </p>
            <Textarea
              rows={3}
              placeholder="Optional note to open the email"
              value={resendNote}
              onChange={(e) => setResendNote(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResendTarget(null)} disabled={resending}>
              Cancel
            </Button>
            <Button onClick={doResend} disabled={resending} data-testid="confirm-resend-invite">
              {resending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
